import {
  Box,
  TextInput,
  Heading,
  Text,
  RadioGroup,
  Radio,
  Link,
  InfoIcon,
  TextArea,
  Divider,
  ArrowRightIcon,
} from '@razorpay/blade/components';

import React from 'react';
import { useFormWithProvider } from 'forms';
import { BladeTooltipWrapper } from 'components/ui/BladeTooltipWrapper/BladeTooltipWrapper';
import { FlexibleBenefitFormData } from './types';
import { overlayLayers } from 'constants/overlayLayers.generated';

export const BasicDetails = ({ isEditFlow = false }: { isEditFlow: boolean }) => {
  const { formData, errors, setFormData } = useFormWithProvider<FlexibleBenefitFormData>();

  const handleChange = (field: string, value: string | boolean) => {
    setFormData({
      ...formData,
      [field]: value,
    });
  };

  const handleSalaryExclusionChange = (value: boolean) => {
    setFormData({
      ...formData,
      showInGrossSalary: value,
      nonCashBenefit: !value ? value : formData.nonCashBenefit,
      ...(!value
        ? {
            hold_settings: {
              ...formData.hold_settings,
              hold_unclaimed_amount: 'true',
            },
            tax_behaviour: {
              tax_exempt_in_new_regime: false,
              tax_exempt_in_old_regime: false,
            },
            arrear_behaviour: {
              ...formData.arrear_behaviour,
              consider_in_arrear: false,
            },
            tax_exemption_section: undefined,
            // SA adjustment only applies to gross components
            sa_adjustment_settings: {
              ...formData.sa_adjustment_settings,
              adjust_to_sa: true,
            },
          }
        : {}),
    });
  };

  return (
    <Box padding="spacing.6" display="flex" flexDirection="column" gap="spacing.6">
      <Box>
        <Heading size="large" weight="semibold" color="surface.text.gray.normal">
          Basic Details
        </Heading>
      </Box>

      <Box display="flex" flexDirection="column" gap="spacing.6" flex={1}>
        <Box display="flex" flexDirection="row">
          <Box flex={1}>
            <Text color="surface.text.gray.subtle" weight="semibold">
              Name
            </Text>
          </Box>
          <Box flex={1} display="flex" flexDirection="column" gap="spacing.6">
            <TextInput
              label="Component name"
              necessityIndicator="required"
              placeholder="Incentive"
              value={formData.name || ''}
              onChange={({ value }) => handleChange('name', value || '')}
              validationState={errors.name ? 'error' : 'none'}
              errorText={errors.name}
              size="medium"
              isDisabled={isEditFlow}
            />
            <TextInput
              label="Display name"
              necessityIndicator="required"
              placeholder="Fuel Allowance"
              value={formData.displayName || ''}
              onChange={({ value }) => handleChange('displayName', value || '')}
              validationState={errors.displayName ? 'error' : 'none'}
              errorText={errors.displayName}
              size="medium"
              trailing={
                <BladeTooltipWrapper content="Component name that will appear in employee’s payslip and Salary Register">
                  <InfoIcon />
                </BladeTooltipWrapper>
              }
              helpText="This name will be visible to employees"
            />
            <TextArea
              label="Component description"
              placeholder="For example, a component designed to help employees with housing or rental expenses."
              value={formData.description || ''}
              onChange={({ value }) => handleChange('description', value || '')}
              size="medium"
            />
          </Box>
        </Box>
        <Divider />

        <Box display="flex" flexDirection="row">
          <Box display="flex" flexDirection="column" gap="spacing.2" flex={1}>
            <Text size="large" weight="semibold" color="surface.text.gray.normal">
              Gross Salary Inclusion
            </Text>
            <Link
              size="small"
              icon={ArrowRightIcon}
              iconPosition="right"
              target="_blank"
              rel="noopener noreferrer"
              variant="anchor"
              href="https://razorpay.com/docs/payroll/compensation/ctc-components/#component-categories">
              Learn more
            </Link>
          </Box>
          <Box display="flex" flexDirection="column" gap="spacing.6" flex={1}>
            <RadioGroup
              name="gross-salary-inclusion"
              size="medium"
              isDisabled={isEditFlow}
              value={formData.showInGrossSalary ? 'true' : 'false'}
              onChange={({ value }) => handleSalaryExclusionChange(value === 'true')}>
              <Radio
                value="true"
                helpText="This benefit will be included in the gross salary calculation and eligible exemptions.">
                Add to Gross Salary
              </Radio>
              <Radio
                value="false"
                helpText="This component will not be included in the gross salary calculation.">
                Exclude from Gross Salary Calculation
              </Radio>
            </RadioGroup>
          </Box>
        </Box>

        <Divider />

        <Box display="flex" flexDirection="row">
          <Box display="flex" flexDirection="column" gap="spacing.2" flex={1}>
            <Text size="large" weight="semibold" color="surface.text.gray.normal">
              Benefit Type
            </Text>
          </Box>
          <Box display="flex" flexDirection="column" gap="spacing.6" flex={1}>
            <RadioGroup
              name="benefit-type"
              size="medium"
              value={formData.nonCashBenefit ? 'true' : 'false'}
              onChange={({ value }) => handleChange('nonCashBenefit', value === 'true')}>
              <Radio
                value="false"
                isDisabled={isEditFlow}
                helpText="The benefit will be paid directly to the employee as part of their salary">
                Cash Benefit
              </Radio>
              <BladeTooltipWrapper
                content={
                  !formData.showInGrossSalary
                    ? 'The benefit will be provided in a non-monetary form (e.g., meal card, NPS contribution, gift voucher) and will not be included in the cash payout.'
                    : ''
                }
                zIndex={overlayLayers.nested}
                placement="top-start"
                tooltipInteractiveWrapperProps={{
                  display: 'flex',
                }}
                shouldAddInteractiveWrapper={true}>
                <Radio
                  value="true"
                  isDisabled={!formData.showInGrossSalary || isEditFlow}
                  helpText="The benefit will be provided in a non-monetary form (e.g., meal card, NPS contribution, gift voucher) and will not be included in the cash payout.">
                  Non-Cash Benefit
                </Radio>
              </BladeTooltipWrapper>
            </RadioGroup>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
